"use client";

/**
 * Ce que le morceau fait sonner — pas qui joue.
 *
 * L'axe est volontairement « ce que j'entends », parce qu'il couvre les deux
 * usages réels avec le même état : écouter une main seule pour entendre ce
 * qu'elle fait, ou travailler une main pendant que l'application tient
 * l'autre. Dans les deux cas on désigne la main qui doit sonner, et
 * l'interface n'a jamais à savoir où sont les mains de l'utilisateur.
 *
 * Ce contrôle ne touche jamais à la visibilité des notes. Sur le corpus,
 * 39,9 % des groupes de notes ont une main qui frappe pendant que l'autre
 * tient : la main qu'on n'écoute pas porte le repère du *quand*, la retirer
 * de l'écran rendrait une note sur deux inexplicable.
 */
export type HandMode = "none" | "left" | "both" | "right";

interface HandSelectorProps {
  value: HandMode;
  onChange: (v: HandMode) => void;
  /**
   * Ajoute le segment « Rien » (le morceau se tait entièrement).
   * Absent par défaut : un sélecteur de main n'a pas d'état muet.
   */
  withSilence?: boolean;
}

type Option = {
  mode: HandMode;
  label: string;
  color: string;
  title: string;
};

const SILENCE_OPTION: Option = {
  mode: "none",
  // Neutre et non l'une des deux couleurs de main : le silence n'appartient
  // à aucune des deux.
  color: "var(--color-text-primary)",
  label: "Rien",
  title: "N'écouter que soi, le morceau se tait",
};

const HAND_OPTIONS: readonly Option[] = [
  {
    mode: "left",
    label: "MG",
    color: "var(--color-hand-left)",
    title: "Écouter la main gauche seule",
  },
  {
    mode: "both",
    label: "Deux",
    color: "var(--color-accent)",
    title: "Écouter les deux mains",
  },
  {
    mode: "right",
    label: "MD",
    color: "var(--color-hand-right)",
    title: "Écouter la main droite seule",
  },
];

export function HandSelector({
  value,
  onChange,
  withSilence = false,
}: HandSelectorProps) {
  const options = withSilence ? [SILENCE_OPTION, ...HAND_OPTIONS] : HAND_OPTIONS;

  return (
    <div
      role="radiogroup"
      aria-label="Écouter"
      // Le contrôle remplit son conteneur et les segments se partagent la
      // largeur. Mesuré : à quatre segments la largeur naturelle est de 175 px
      // pour 176 px utiles dans la sidebar — une marge d'1 px qu'une police de
      // repli suffirait à faire déborder. Ainsi il ne peut pas déborder, et
      // dans une colonne à largeur fixe c'est aussi ce qui se lit le mieux.
      className="flex h-7 w-full rounded-lg overflow-hidden"
      style={{ border: "1px solid rgba(255, 255, 255, 0.06)" }}
    >
      {options.map(({ mode, label, color, title }, index) => {
        const isActive = value === mode;

        return (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => onChange(mode)}
            // `px-2` et non `px-3` : à quatre segments, c'est ce qui garde le
            // texte au large dans les 184 px utiles de la sidebar.
            className="flex-1 px-2 text-[11px] font-medium transition-all duration-150 cursor-pointer"
            style={{
              backgroundColor: isActive
                ? `color-mix(in srgb, ${color} 18%, transparent)`
                : "transparent",
              // 0.55 et non le `text-muted` d'origine (0.35), qui tombait à
              // 2,9:1 sur `bg-surface` — sous le seuil WCAG de 4,5:1 pour des
              // libellés qu'il faut lire pour choisir. À 0.55 on est à 5,4:1,
              // et l'état actif reste distinct par sa couleur et son fond.
              color: isActive ? color : "rgba(255, 255, 255, 0.55)",
              borderRight:
                index < options.length - 1
                  ? "1px solid rgba(255, 255, 255, 0.06)"
                  : "none",
            }}
            title={title}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
